import { useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { useEntitlement, useNodes } from "../../api/queries";
import type { FleetNode } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { NodeStatusPill } from "../../components/StatePill";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { CapacityNotice } from "../billing/CapacityNotice";
import { nodeCapacityNotice } from "../billing/entitlementRules";
import { useFleet } from "../../fleet/FleetContext";
import { AddHostModal } from "./AddHostModal";

const COLUMNS: DataTableColumn<FleetNode>[] = [
  { key: "name", header: "Name", cell: (node) => node.name, className: "data-table__primary" },
  { key: "status", header: "Status", cell: (node) => <NodeStatusPill status={node.status} /> },
  { key: "hostname", header: "Hostname", cell: (node) => node.hostname, className: "data-table__mono" },
  {
    key: "agentVersion",
    header: "Agent version",
    cell: (node) => node.agentVersion ?? "not enrolled",
    className: (node) => (node.agentVersion ? "data-table__mono" : "data-table__empty-cell"),
  },
  { key: "containers", header: "Containers", cell: (node) => node.containerCount },
  {
    key: "lastSeen",
    header: "Last seen",
    cell: (node) => (node.lastSeenAt ? new Date(node.lastSeenAt).toLocaleString() : "never"),
    className: "data-table__secondary",
  },
];

export function NodesListPage() {
  const { currentId, isLoading: fleetLoading } = useFleet();
  const { session } = useAuth();
  const paginator = usePaginator(currentId);
  const { data, isPending, isError, error, refetch } = useNodes(currentId, { cursor: paginator.cursor });
  const [addHostOpen, setAddHostOpen] = useState(false);

  // Minting a join token is node:create, which the authz matrix grants to
  // admin only (server/internal/authz) — an operator/viewer sees a note
  // instead of a control they'd just get a 403 from.
  const isAdmin = session?.role === "admin";

  // Licensed node capacity is tenant-wide, not per-fleet (server/internal/
  // entitlements), so this notice is the same regardless of which fleet is
  // selected above — it's about "can I add a host at all," not "in this
  // fleet." Gated on isAdmin: only admin holds node:create AND billing:view
  // (see roleView.ts), so this never fires a request an operator/viewer
  // could only get a 403 from.
  const { data: entitlement } = useEntitlement({ enabled: isAdmin });
  const capacityNotice = entitlement ? nodeCapacityNotice(entitlement) : null;

  return (
    <>
      <PageHeader
        title="Nodes"
        description="Hosts enrolled in this fleet, each running one Kelpie agent over an outbound mTLS tunnel."
        actions={
          isAdmin ? (
            <Button variant="primary" onClick={() => setAddHostOpen(true)}>
              Add a host
            </Button>
          ) : (
            <span className="page-header__hint">Ask an admin to add hosts.</span>
          )
        }
      />

      {isAdmin && capacityNotice && <CapacityNotice severity={capacityNotice.severity} message={capacityNotice.message} />}

      <QueryStates
        isLoading={fleetLoading || isPending}
        loadingLabel="Loading nodes…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={error instanceof Error ? error.message : "Couldn't load nodes. Check your connection and try again."}
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No nodes enrolled yet"
            description="Enroll a host with a join token to bring it under management."
            action={
              isAdmin ? (
                <Button variant="primary" onClick={() => setAddHostOpen(true)}>
                  Add a host
                </Button>
              ) : undefined
            }
          />
        }
      >
        {data && (
          <>
            <DataTable columns={COLUMNS} rows={data.items} rowKey={(node) => node.id} />
            <PaginatorControls itemCount={data.items.length} nextCursor={data.nextCursor} paginator={paginator} noun="nodes" />
          </>
        )}
      </QueryStates>

      {isAdmin && <AddHostModal open={addHostOpen} fleetId={currentId} onClose={() => setAddHostOpen(false)} />}
    </>
  );
}
